import React, { useState } from 'react';
import { Plus, Trash2, Upload, Crop } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import type { FeaturesData, Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ImageCropModal } from '../shared/ImageCropModal';
import type { CropData } from '../shared/ImageCropModal';
import type { Area } from 'react-easy-crop';
import { getCroppedImageStyles, hasCropApplied } from '../shared/cropUtils';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';

interface FeaturesEditorProps {
  formData: FeaturesData;
  setFormData: (data: FeaturesData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (index: number) => void;
  currentPageComponents?: Component[];
}

const ICON_OPTIONS = [
  { value: 'star', label: 'Star' },
  { value: 'zap', label: 'Lightning' },
  { value: 'shield', label: 'Shield' },
  { value: 'globe', label: 'Globe' },
  { value: 'smartphone', label: 'Mobile' },
  { value: 'palette', label: 'Design' },
  { value: 'code', label: 'Code' },
  { value: 'users', label: 'Users' },
  { value: 'heart', label: 'Heart' },
  { value: 'lightbulb', label: 'Lightbulb' },
  { value: 'target', label: 'Target' },
  { value: 'rocket', label: 'Rocket' },
];

export const FeaturesEditor: React.FC<FeaturesEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData;
  const buttons = (data as any).buttons || [];
  const themeColors = useCustomColors();

  const addFeature = () => {
    const newFeature = {
      id: uuidv4(),
      title: 'New Feature',
      description: 'Feature description',
      icon: 'star',
      imageUrl: '',
      imageAlt: '',
      order: (data.features || []).length,
    };
    setFormData({
      ...data,
      features: [...(data.features || []), newFeature],
    });
  };

  const updateFeature = (index: number, updates: any) => {
    const newFeatures = [...(data.features || [])];
    newFeatures[index] = {
      ...newFeatures[index],
      ...updates,
      order: updates.order !== undefined ? updates.order : (newFeatures[index].order ?? index),
    };
    setFormData({ ...data, features: newFeatures });
  };

  const removeFeature = (index: number) => {
    const newFeatures = (data.features || []).filter((_, i) => i !== index);
    const reorderedFeatures = newFeatures.map((feat, i) => ({
      ...feat,
      order: i,
    }));
    setFormData({ ...data, features: reorderedFeatures });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('features', themeColors);

  return (
    <div className="space-y-6">
      {/* Title and Subtitle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title *</label>
        <textarea
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Enter section title (press Enter for line break)"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle (Optional)</label>
        <textarea
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Enter section subtitle"
        />
      </div>

      {/* Layout Options */}
      <LayoutOptions data={data} setFormData={setFormData} />

      {/* Features List */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">Features</label>
          <button
            type="button"
            onClick={addFeature}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
          >
            <Plus size={16} />
            <span>Add Feature</span>
          </button>
        </div>

        <div className="space-y-4">
          {(data.features || []).map((feature, index) => (
            <FeatureItem
              key={index}
              feature={feature}
              index={index}
              displayMode={data.displayMode}
              onUpdate={updates => updateFeature(index, updates)}
              onRemove={() => removeFeature(index)}
              onUploadImage={() => onOpenMediaUpload(index)}
            />
          ))}
        </div>
      </div>

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={(newButtons: ButtonData[]) =>
          setFormData({ ...data, buttons: newButtons } as FeaturesData)
        }
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize features section colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface LayoutOptionsProps {
  data: FeaturesData;
  setFormData: (data: FeaturesData) => void;
}

const LayoutOptions: React.FC<LayoutOptionsProps> = ({ data, setFormData }) => (
  <>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
        <select
          value={data.layout || 'grid'}
          onChange={e =>
            setFormData({ ...data, layout: e.target.value as 'grid' | 'list' | 'carousel' })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="grid">Grid</option>
          <option value="list">List</option>
          <option value="carousel">Carousel</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Columns (Grid only)</label>
        <select
          value={data.columns || '3'}
          onChange={e => setFormData({ ...data, columns: e.target.value as '1' | '2' | '3' | '4' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          disabled={data.layout !== 'grid'}
        >
          <option value="1">1 Column</option>
          <option value="2">2 Columns</option>
          <option value="3">3 Columns</option>
          <option value="4">4 Columns</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Display Mode</label>
        <select
          value={data.displayMode || 'icon'}
          onChange={e =>
            setFormData({ ...data, displayMode: e.target.value as 'icon' | 'image' | 'both' })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="icon">Icon Only</option>
          <option value="image">Image Only</option>
        </select>
      </div>
    </div>

    {(data.displayMode === 'image' || data.displayMode === 'both') && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Image Shape</label>
          <select
            value={data.imageShape || 'rounded'}
            onChange={e =>
              setFormData({
                ...data,
                imageShape: e.target.value as 'square' | 'circle' | 'rounded',
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="square">Square</option>
            <option value="circle">Circle</option>
            <option value="rounded">Rounded</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Image Size</label>
          <select
            value={data.imageSize || 'medium'}
            onChange={e =>
              setFormData({ ...data, imageSize: e.target.value as 'small' | 'medium' | 'large' })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
      </div>
    )}
  </>
);

interface FeatureItemProps {
  feature: any;
  index: number;
  displayMode?: string;
  onUpdate: (updates: any) => void;
  onRemove: () => void;
  onUploadImage: () => void;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  feature,
  index,
  displayMode,
  onUpdate,
  onRemove,
  onUploadImage,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const displayUrl = feature.cloudfrontUrl || feature.imageUrl;

  const handleCropApply = (crop: CropData, _croppedAreaPixels: Area) => {
    onUpdate({ crop });
    setIsCropModalOpen(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-start mb-3">
        <h4 className="text-sm font-medium text-gray-700">Feature {index + 1}</h4>
        <button type="button" onClick={onRemove} className="text-red-600 hover:text-red-700">
          <Trash2 size={16} />
        </button>
      </div>

      <div className="space-y-3">
        <textarea
          value={feature.title}
          onChange={e => onUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Feature title"
        />

        <textarea
          value={feature.description}
          onChange={e => onUpdate({ description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Feature description"
        />

        <div className="flex space-x-2">
          {(displayMode === 'icon' || displayMode === 'both' || !displayMode) && (
            <div className="flex-1">
              <label className="block text-xs text-gray-600 mb-1">Icon</label>
              <select
                value={feature.icon || 'star'}
                onChange={e => onUpdate({ icon: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                {ICON_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="w-20">
            <label className="block text-xs text-gray-600 mb-1">Order</label>
            <input
              type="number"
              value={feature.order ?? index}
              onChange={e => {
                const value = e.target.value;
                const numValue = value === '' ? 0 : parseInt(value, 10);
                onUpdate({ order: !isNaN(numValue) ? numValue : 0 });
              }}
              className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              min="0"
            />
          </div>
        </div>

        {(displayMode === 'image' || displayMode === 'both') && (
          <div className="space-y-2">
            <label className="block text-xs text-gray-600">Feature Image</label>
            {displayUrl ? (
              <div className="flex items-start space-x-3">
                {/* Thumbnail with crop indicator */}
                <div className="relative group">
                  <div className="w-16 h-16 rounded-md overflow-hidden border border-gray-300 bg-gray-100 relative">
                    <img
                      src={displayUrl}
                      alt={feature.imageAlt || 'Feature image'}
                      className={
                        hasCropApplied(feature.crop) ? undefined : 'w-full h-full object-cover'
                      }
                      style={
                        hasCropApplied(feature.crop)
                          ? getCroppedImageStyles(feature.crop)
                          : undefined
                      }
                    />
                  </div>
                  {/* Crop indicator badge */}
                  {feature.crop && feature.crop.zoom !== 1 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center shadow-sm">
                      <Crop size={10} className="text-white" />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setIsCropModalOpen(true)}
                      className="px-2 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded hover:bg-primary-100 flex items-center space-x-1"
                    >
                      <Crop size={12} />
                      <span>Crop</span>
                    </button>
                    <button
                      type="button"
                      onClick={onUploadImage}
                      className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 flex items-center space-x-1"
                    >
                      <Upload size={12} />
                      <span>Change</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        onUpdate({
                          imageUrl: '',
                          cloudfrontUrl: '',
                          imageAlt: '',
                          crop: undefined,
                        })
                      }
                      className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 flex items-center space-x-1"
                    >
                      <Trash2 size={12} />
                      <span>Remove</span>
                    </button>
                  </div>
                  <input
                    type="text"
                    value={feature.imageAlt || ''}
                    onChange={e => onUpdate({ imageAlt: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                    placeholder="Image alt text"
                  />
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={onUploadImage}
                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md hover:border-primary-500 flex items-center justify-center space-x-2 text-gray-600 hover:text-primary-600"
              >
                <Upload size={16} />
                <span>Upload Image</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Crop Modal */}
      {displayUrl && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageUrl={displayUrl}
          initialCrop={feature.crop}
          lockedAspect={1}
          onApply={handleCropApply}
        />
      )}
    </div>
  );
};
